import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Plus, Mic, Square, Trash2, Pencil, Save, X, ListChecks, Filter,
} from "lucide-react";
import { toast } from "sonner";

const BASE_LABELS = { mcq: "Multiple choice", short: "Short answer", long: "Long answer" };
const EMPTY = { label: "", base: "short", subjects: [], instruction: "" };

export default function UtilityAdminQuestionTypes({ embedded } = {}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filter, setFilter] = useState("__all");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const load = useCallback(async (subj) => {
    setLoading(true);
    try {
      const params = subj && subj !== "__all" ? { subject: subj } : {};
      const { data } = await api.get("/question-types", { params });
      setTypes(data.types || []);
      setSubjects(data.subjects || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ label: t.label, base: t.base, subjects: t.subjects || [], instruction: t.instruction || "" });
  };

  const toggleSubject = (s) =>
    setForm((f) => ({ ...f, subjects: f.subjects.includes(s) ? f.subjects.filter((x) => x !== s) : [...f.subjects, s] }));

  const save = async () => {
    if (!form.label.trim()) return toast.error("Enter a type name");
    setSaving(true);
    try {
      if (editingId) await api.put(`/question-types/${editingId}`, form);
      else await api.post("/question-types", form);
      toast.success(editingId ? "Type updated" : "Type added");
      resetForm();
      load(filter);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete question type "${t.label}"?`)) return;
    try {
      await api.delete(`/question-types/${t.id}`);
      toast.success("Deleted");
      if (editingId === t.id) resetForm();
      load(filter);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete");
    }
  };

  // ---- Voice → text for the type name ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribe(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); }
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      fd.append("language", "auto");
      const { data } = await api.post("/question-types/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data.text || "").trim();
      if (text) { setForm((f) => ({ ...f, label: text })); toast.success("Transcribed — edit if needed"); }
      else toast.message("Didn't catch that, try again");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Transcription failed");
    } finally { setTranscribing(false); }
  };

  const Wrap = embedded ? Fragment : Layout;
  return (
    <Wrap>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="utility-question-types">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-emerald-500" /> Question Types
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Define custom question types (beyond MCQ / Short / Long), scoped by subject. Teachers see them in the
            exam builder's Type dropdown — filtered by the paper's subject.
          </p>
        </div>

        {/* Editor */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {editingId ? "Edit type" : "Add a new type"}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-500">Type name</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Fill in the Blanks, Match the Following, True / False"
                  className="rounded-xl" data-testid="qt-label-input"
                />
                <Button
                  type="button" variant={recording ? "destructive" : "outline"}
                  className="rounded-xl shrink-0"
                  onClick={recording ? stopRecording : startRecording}
                  disabled={transcribing}
                  data-testid="qt-voice-btn"
                  title={recording ? "Stop & transcribe" : "Speak the type name"}
                >
                  {transcribing ? <Loader2 className="h-4 w-4 animate-spin" />
                    : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              </div>
              {recording && <p className="text-xs text-red-500 mt-1 animate-pulse">● Recording… tap ■ to stop</p>}
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">Behaves like</label>
              <Select value={form.base} onValueChange={(v) => setForm((f) => ({ ...f, base: v }))}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="qt-base-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(BASE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">Subjects (none selected = all subjects)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {subjects.map((s) => {
                const on = form.subjects.includes(s);
                return (
                  <button
                    key={s} type="button" onClick={() => toggleSubject(s)}
                    data-testid={`qt-subject-${s}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      on ? "bg-emerald-500 text-white border-emerald-500"
                         : "bg-transparent text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-emerald-400"}`}
                  >{s}</button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">AI formatting hint (optional)</label>
            <Textarea
              value={form.instruction}
              onChange={(e) => setForm((f) => ({ ...f, instruction: e.target.value }))}
              placeholder='e.g. "Give a sentence with a blank shown as ______ ; the answer is the missing word."'
              className="rounded-xl mt-1" rows={2} data-testid="qt-instruction-input"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="qt-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : editingId ? <Save className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              {editingId ? "Save changes" : "Add type"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm} className="rounded-full" data-testid="qt-cancel-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Configured types</div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="rounded-xl w-44 h-9" data-testid="qt-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All subjects</SelectItem>
                  {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : types.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No custom types yet. Add one above.</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {types.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 py-3" data-testid={`qt-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{t.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Behaves like <b>{BASE_LABELS[t.base] || t.base}</b> ·{" "}
                      {t.subjects?.length ? t.subjects.join(", ") : "All subjects"}
                    </div>
                    {t.instruction && <div className="text-xs text-slate-400 mt-0.5 italic truncate max-w-xl">{t.instruction}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(t)} data-testid={`qt-edit-${t.id}`}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(t)} data-testid={`qt-delete-${t.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Wrap>
  );
}
